import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { placeLabel } from 'utils/meetPoints';
import {
  MAX_PLACES,
  copyPointsRules,
  pointsRulesExample,
  pointsRulesProblems,
  pointsRulesSummary,
  startingRelays,
  TIES_CHOICES,
  TIES_SETTING,
} from 'utils/pointsRules';
import { formatPoints } from 'utils/points';
import { TEAM_LABEL, teamWords as wordsForLabel } from 'utils/teamLabel';
import { ReactComponent as InfoIcon } from 'assets/images/icon_info_circle.svg';
import styles from './PointsRulesEditor.module.css';

// The points settings of the mock-up Doug approved (docs/school-carnivals-points-slice-2.md in the
// portal repo): the points for each place, the points for taking part in an event and who earns them,
// and More options; beside them a summary in plain words with a worked example, which follows every
// change, and any problem that would stop Save. Used by Club Settings (the club's points) and by Edit
// Meet (a meet's own points). The rules are the API's PointsRulesDto; see utils/pointsRules.js.
//
// The ties choice (slice 3) is in More options, for Club Settings only (Doug: a meet keeps the club's
// rule, and its own points do not offer one). The Relays card (slice 4a) is for an organisation with
// houses, in Club Settings and in a meet's own points, in the organisation's word for a house.

// A number box's value as typed: '' while empty, otherwise the number (the checks refuse anything
// that is not a whole number in range).
const typed = (value) => (value === '' ? '' : Number(value));

const PointsRulesEditor = ({
  rules = null,
  onChange,
  forClub = false,
  seasonBestPoints = true,
  idPrefix = 'points',
  hasHouses = false,
  teamWords = wordsForLabel(TEAM_LABEL.TEAM),
}) => {
  const { words } = useGroupLabels();
  const groupWord = words.lower.group;
  const house = teamWords.lower.team;
  // The relay list last shown, so unticking and ticking again before Save gives it back.
  const lastRelays = useRef(null);

  if (!rules) return null;

  const places = rules.placePoints || [];
  const update = (changes) => onChange({ ...copyPointsRules(rules), ...changes });

  const setPlace = (index, value) => {
    const next = [...places];
    next[index] = typed(value);
    update({ placePoints: next });
  };

  // A new place starts at the last place's points, which never breaks a check; the admin lowers it.
  const addPlace = () => {
    const last = places[places.length - 1];
    update({ placePoints: [...places, Number.isInteger(last) && last > 0 ? last : 1] });
  };

  const removePlace = () => update({ placePoints: places.slice(0, -1) });

  // Relays (slice 4a). Ticking the box with no relay list yet starts it with the place points (Doug,
  // 2026-10-24); unticking it removes the list from what is saved.
  const relays = rules.relays || null;
  const relayPlaces = relays?.placePoints || [];
  const setRelays = (changes) => update({ relays: { ...relays, ...changes } });

  const toggleRelays = (on) => {
    if (on) {
      update({ relays: lastRelays.current || startingRelays(rules) });
    } else {
      lastRelays.current = relays;
      update({ relays: null });
    }
  };

  const setRelayPlace = (index, value) => {
    const next = [...relayPlaces];
    next[index] = typed(value);
    setRelays({ placePoints: next });
  };

  const addRelayPlace = () => {
    const last = relayPlaces[relayPlaces.length - 1];
    setRelays({ placePoints: [...relayPlaces, Number.isInteger(last) && last > 0 ? last : 1] });
  };

  const removeRelayPlace = () => setRelays({ placePoints: relayPlaces.slice(0, -1) });

  // The club's first-result choice: a first result earns no season-best points, so the Set box is
  // not used (shown as 0, as Club Settings always has).
  const excludeFirst = Boolean(rules.excludeFirstSeasonBest);

  const summary = pointsRulesSummary(rules, { groupWord, seasonBestPoints, houseWord: house });
  const example = pointsRulesExample(rules);
  const problems = pointsRulesProblems(rules, { seasonBestPoints, relaysOffered: hasHouses });

  return (
    <div className={styles.editor}>
      <div className={styles.fields}>
        <section className={styles.block}>
          <h4 className={styles.blockTitle}>Points for each place</h4>
          <p className={styles.help}>
            {hasHouses
              ? `Place points go to the athletes placed in their ${groupWord}, and count for their ${house}. `
              : `Place points go to the athletes placed in their ${groupWord}. `}
            Add as many places as you score.
          </p>
          <div className={styles.places}>
            {places.map((points, index) => (
              <label key={index} className={styles.place}>
                <span>{placeLabel(index + 1)}</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={points}
                  onChange={(e) => setPlace(index, e.target.value)}
                  aria-label={`${placeLabel(index + 1)} place points`}
                />
              </label>
            ))}
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.addButton}
              onClick={addPlace}
              disabled={places.length >= MAX_PLACES}
            >
              + Add a place
            </button>
            <button
              type="button"
              className={styles.linkButton}
              onClick={removePlace}
              disabled={places.length <= 1}
            >
              Remove the last place
            </button>
          </div>
        </section>

        <section className={styles.block}>
          <h4 className={styles.blockTitle}>Points for taking part in an event</h4>
          <p className={styles.help}>
            Taking part includes a DNF on the track and a field event with every attempt a foul.
          </p>
          <label className={styles.field}>
            <span>Points for each event</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={rules.eventTakingPartPoints}
              onChange={(e) => update({ eventTakingPartPoints: typed(e.target.value) })}
            />
          </label>
          <div className={styles.choices} role="radiogroup" aria-label="Who earns them">
            <label className={styles.choice}>
              <input
                type="radio"
                name={`${idPrefix}-takingPart`}
                checked={!rules.takingPartForEveryone}
                onChange={() => update({ takingPartForEveryone: false })}
              />
              <span>
                <b>Only athletes who don&apos;t earn place points</b>
                <span className={styles.muted}>No one gets both.</span>
              </span>
            </label>
            <label className={styles.choice}>
              <input
                type="radio"
                name={`${idPrefix}-takingPart`}
                checked={Boolean(rules.takingPartForEveryone)}
                onChange={() => update({ takingPartForEveryone: true })}
              />
              <span>
                <b>Everyone who takes part, on top of any place points</b>
                <span className={styles.muted}>A placed athlete earns both.</span>
              </span>
            </label>
          </div>
        </section>

        {hasHouses && (
          <section className={styles.block}>
            <h4 className={styles.blockTitle}>Relays</h4>
            {/* Doug, 2026-10-24 (H3): say first what a relay team does earn, place points for its house. */}
            <p className={styles.help}>
              A relay team earns place points for its {house}. It earns no points for taking part, and its
              runners earn no points themselves.
            </p>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={Boolean(relays)}
                onChange={(e) => toggleRelays(e.target.checked)}
              />
              <span>Relays score points for the {house}</span>
            </label>
            {relays && (
              <>
                <div className={styles.places}>
                  {relayPlaces.map((points, index) => (
                    <label key={index} className={styles.place}>
                      <span>{placeLabel(index + 1)}</span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={points}
                        onChange={(e) => setRelayPlace(index, e.target.value)}
                        aria-label={`Relay ${placeLabel(index + 1)} place points`}
                      />
                    </label>
                  ))}
                </div>
                <div className={styles.row}>
                  <button
                    type="button"
                    className={styles.addButton}
                    onClick={addRelayPlace}
                    disabled={relayPlaces.length >= MAX_PLACES}
                  >
                    + Add a place
                  </button>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={removeRelayPlace}
                    disabled={relayPlaces.length <= 1}
                  >
                    Remove the last place
                  </button>
                </div>
                <div
                  className={styles.choices}
                  role="radiogroup"
                  aria-label={`When a ${house} has more than one team in a relay event`}
                >
                  {/* Doug's words, 2026-10-24 (H6): the rule looks at the round that scores, the highest
                      round held (heats placed together on time when there is no final). */}
                  <h5 className={styles.subTitle}>When a {house} has more than one team in a relay event</h5>
                  <label className={styles.choice}>
                    <input
                      type="radio"
                      name={`${idPrefix}-relayTeams`}
                      checked={!relays.bestTeamOnly}
                      onChange={() => setRelays({ bestTeamOnly: false })}
                    />
                    <span>
                      <b>Every team scores</b>
                    </span>
                  </label>
                  <label className={styles.choice}>
                    <input
                      type="radio"
                      name={`${idPrefix}-relayTeams`}
                      checked={Boolean(relays.bestTeamOnly)}
                      onChange={() => setRelays({ bestTeamOnly: true })}
                    />
                    <span>
                      <b>Only its best-placed team scores</b>
                    </span>
                  </label>
                </div>
              </>
            )}
          </section>
        )}

        <details className={styles.more}>
          <summary>More options</summary>
          <div className={styles.moreBody}>
            {forClub && (
              <div className={`${styles.choices} ${styles.ties}`} role="radiogroup" aria-label="Ties">
                <h5 className={styles.subTitle}>{TIES_SETTING}</h5>
                <label className={styles.choice}>
                  <input
                    type="radio"
                    name={`${idPrefix}-ties`}
                    checked={!rules.sharedTies}
                    onChange={() => update({ sharedTies: false })}
                  />
                  <span>
                    <b>{TIES_CHOICES.full.name}</b>
                    <span className={styles.muted}>{TIES_CHOICES.full.detail}</span>
                  </span>
                </label>
                <label className={styles.choice}>
                  <input
                    type="radio"
                    name={`${idPrefix}-ties`}
                    checked={Boolean(rules.sharedTies)}
                    onChange={() => update({ sharedTies: true })}
                  />
                  <span>
                    <b>{TIES_CHOICES.shared.name}</b>
                    <span className={styles.muted}>{TIES_CHOICES.shared.detail}</span>
                  </span>
                </label>
                {/* Changing it places this season's meets again (slice 3, option B): a popup says so when
                    Save is pressed, before the PIN (Club Settings). No line on the page (Doug, 2026-10-24,
                    T2: "just cluttering the page and nobody will read it"). */}
              </div>
            )}
            <label className={styles.field}>
              <span>Points for taking part in the meet</span>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={rules.meetTakingPartPoints}
                onChange={(e) => update({ meetTakingPartPoints: typed(e.target.value) })}
              />
              <span className={styles.muted}>Once per meet, for anyone who took part in an event.</span>
            </label>

            {seasonBestPoints && (
              <div className={styles.seasonBests}>
                <h5 className={styles.subTitle}>Season-best points</h5>
                <div className={styles.places}>
                  <label className={styles.place}>
                    <span>Set</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={excludeFirst ? 0 : rules.setSeasonBestPoints}
                      disabled={excludeFirst}
                      onChange={(e) => update({ setSeasonBestPoints: typed(e.target.value) })}
                    />
                  </label>
                  <label className={styles.place}>
                    <span>Equal</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={rules.equalSeasonBestPoints}
                      onChange={(e) => update({ equalSeasonBestPoints: typed(e.target.value) })}
                    />
                  </label>
                  <label className={styles.place}>
                    <span>Beat</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={rules.beatSeasonBestPoints}
                      onChange={(e) => update({ beatSeasonBestPoints: typed(e.target.value) })}
                    />
                  </label>
                </div>
                {forClub ? (
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={excludeFirst}
                      onChange={(e) =>
                        update({
                          excludeFirstSeasonBest: e.target.checked,
                          ...(e.target.checked ? { setSeasonBestPoints: 0 } : {}),
                        })
                      }
                    />
                    <span>No season-best points for an athlete&apos;s first result in an event</span>
                  </label>
                ) : (
                  excludeFirst && (
                    <span className={styles.muted}>
                      Your club gives no season-best points for an athlete&apos;s first result in an event
                      (Club Settings).
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        </details>
      </div>

      <aside className={styles.summary} aria-live="polite">
        <h4 className={styles.blockTitle}>How a result scores</h4>
        <ul className={styles.rules}>
          {summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <h5 className={styles.subTitle}>Example: one athlete&apos;s meet</h5>
        <table className={styles.example}>
          <tbody>
            {example.rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.detail}</td>
                <td className={styles.points}>{formatPoints(row.points)}</td>
              </tr>
            ))}
            <tr className={styles.total}>
              <td>Total</td>
              <td />
              <td className={styles.points}>{formatPoints(example.total)}</td>
            </tr>
          </tbody>
        </table>
        {/* A school: how its house points are worked out, in a box that stands out (Doug, 2026-10-24, H8:
            the line under the example was "at the very bottom in tiny font"; the words agreed the same day,
            the ranking sentence taken out at the retest). */}
        {hasHouses ? (
          <div className={styles.houseNote} role="note">
            <InfoIcon className={styles.houseNoteIcon} aria-hidden="true" />
            <span>
              <b className={styles.houseNoteTitle}>{teamWords.team} points</b>
              A {house}&apos;s total is its athletes&apos; points{relays ? ', its relay points' : ''} and any bonus points.
            </span>
          </div>
        ) : (
          <p className={styles.muted}>The same points show on the meet&apos;s Points page and in the season total.</p>
        )}
        {problems.length > 0 && (
          <div className={styles.problems} role="alert">
            <b>To fix before saving</b>
            <ul>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
};

PointsRulesEditor.propTypes = {
  rules: PropTypes.shape({
    placePoints: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    takingPartForEveryone: PropTypes.bool,
    eventTakingPartPoints: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    meetTakingPartPoints: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setSeasonBestPoints: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    equalSeasonBestPoints: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    beatSeasonBestPoints: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    excludeFirstSeasonBest: PropTypes.bool,
    sharedTies: PropTypes.bool,
    relays: PropTypes.shape({
      placePoints: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
      bestTeamOnly: PropTypes.bool,
    }),
  }),
  onChange: PropTypes.func.isRequired,
  // Club Settings: the first-result choice can be changed here. A meet's own points carry the club's.
  forClub: PropTypes.bool,
  // The organisation is offered season-best points (clubFeaturesOf(club).seasonBestPoints).
  seasonBestPoints: PropTypes.bool,
  // Keeps the radio buttons of two editors on one page apart.
  idPrefix: PropTypes.string,
  // The organisation has houses (clubFeaturesOf(club).teams): the Relays card shows.
  hasHouses: PropTypes.bool,
  // The organisation's words for a house (utils/teamLabel.js).
  teamWords: PropTypes.shape({
    team: PropTypes.string,
    teams: PropTypes.string,
    lower: PropTypes.shape({ team: PropTypes.string }),
  }),
};

export default PointsRulesEditor;
